import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { crm } from "../../../theme";
import { PurchaseOrderViewModel } from "../../../models/PurchaseOrder";
import { DateTime } from "luxon";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import {
  FaArchive,
  FaDownload,
  FaHistory,
  FaPencilAlt,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import PurchaseOrderPrint from "../save/components/PurchaseOrderPrint";
import {
  removePurchaseOrder,
  updateProcess,
} from "../../../services/purchaseOrderService";
import { useNavigate } from "react-router-dom";
import ProcessInput from "../save/components/ProcessInput";
import ApproveModal from "../../processes/components/approveModal";
import { canManagePurchaseOrders } from "../permissions";

const PurchaseOrderCard = ({
  p,
  get,
}: {
  p: PurchaseOrderViewModel;
  onOpen: () => void;
  get: () => Promise<void>;
}) => {
  const auth = useAuthUser<any>();

  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [loadingProcess, setLoadingProcess] = useState(false);
  const [processId, setProcessId] = useState<string>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useDisclosure();
  const toast = useToast();
  const canManage = canManagePurchaseOrders(auth);
  const hasProcess = !!p.process?.cvfRef;
  const setProcess = async () => {
    setLoadingProcess(true);
    try {
      const response = await updateProcess(p?.id ?? "", processId ?? "");
      if (!response?.data?.success) {
        toast({
          title: response?.data?.notifications?.[0] ?? "Erro ao vincular processo.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      await get();
      onClose();
    } catch {
      toast({ title: "Erro ao vincular processo.", status: "error", duration: 5000 });
    } finally {
      setLoadingProcess(false);
    }
  };

  const formatterUsd = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
  });

  const printRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleRemove = async () => {
    setLoadingDelete(true);
    await removePurchaseOrder(p?.id ?? "");
    setLoadingDelete(false);
    await get();
  };
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Ordem de Compra - ${p.client || ""}`,
    pageStyle: `
        @page {
          size: A4;
          margin: 5mm; 
        }
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          .page-break {
            page-break-before: always;
          }
          .avoid-break {
            page-break-inside: avoid;
          }
        }
      `,
    onBeforePrint: async () => {
      setLoadingDownload(true);
    },
    onAfterPrint: () => {
      setLoadingDownload(false);
    },
  });

  return (
    <Flex
      flexDir={{ base: "column", md: "row" }}
      p="16px"
      gap="24px"
      borderRadius="12px"
      backgroundColor={crm.surface}
      border={`1px solid ${crm.border}`}
      boxShadow={crm.shadowSm}
      justifyContent="space-between"
      transition="box-shadow 0.15s, border-color 0.15s, transform 0.15s"
      _hover={{ boxShadow: crm.shadowMd, borderColor: crm.brand, transform: "translateY(-1px)" }}
    >
      <Flex>
        <Flex flexDir="column" h="100%" justifyContent="center">
          <Heading ml="32px" mr="64px" size="md" fontWeight="semibold" textTransform="none">
            ALFA {p.process?.cvfRef} / PO {p.poNumber}
          </Heading>
        </Flex>
      </Flex>

      <Flex flexDir="column" mt="10px" w="300px">
        <Heading size="sm" fontWeight="semibold" mb="4px">
          Fornecedor
        </Heading>

        <Heading size="md" fontWeight="normal">
          {p.supplier}
        </Heading>
      </Flex>

      <Flex flexDir="column" mt="10px">
        <Heading size="sm" fontWeight="semibold" mb="4px">
          Cliente
        </Heading>

        <Heading size="md" fontWeight="normal">
          {p.client}
        </Heading>
      </Flex>
      <Flex flexDir="column" mt="10px">
        <Heading size="sm" fontWeight="semibold" mb="4px">
          Total do pedido
        </Heading>

        <Heading size="md" color={crm.danger} fontWeight="normal">
          {formatterUsd.format(p.totalFobValue ?? 0)}
        </Heading>
      </Flex>
      <Flex flexDir="column" mt="10px">
        <Heading size="sm" fontWeight="semibold" mb="4px">
          Total a pagar
        </Heading>

        <Heading size="md" color={crm.success} fontWeight="normal">
          {formatterUsd.format(p.totalToPay ?? 0)}
        </Heading>
      </Flex>
      <Flex flexDir="column" mt="10px">
        <Flex gap={4}>
          <IconButton
            isLoading={loadingDownload}
            onClick={() => handlePrint()}
            variant="ghost"
            color={crm.brand}
            _hover={{ bg: crm.brandSoft }}
            aria-label="Baixar"
            icon={<FaDownload />}
          />
          <Tooltip
            label={
              p.pendingChanges
                ? `${p.pendingChanges} alteraç${p.pendingChanges === 1 ? "ão pendente" : "ões pendentes"} de revisão`
                : "Histórico de alterações"
            }
          >
            <Box position="relative">
              <IconButton
                onClick={history.onOpen}
                variant="ghost"
                color={crm.brand}
                _hover={{ bg: crm.brandSoft }}
                aria-label="Histórico"
                icon={<FaHistory />}
              />
              {!!p.pendingChanges && (
                <Box
                  position="absolute"
                  top="-2px"
                  right="-2px"
                  minW="18px"
                  h="18px"
                  px="4px"
                  borderRadius="full"
                  bg={crm.danger}
                  color="white"
                  fontSize="10px"
                  fontWeight="700"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="none"
                >
                  {p.pendingChanges}
                </Box>
              )}
            </Box>
          </Tooltip>
          {canManage && !hasProcess && (
            <Tooltip label="Criar processo a partir desta PO">
              <IconButton
                onClick={() => navigate(`/saveProcess?fromPo=${p.id}`)}
                variant="ghost"
                color={crm.success}
                _hover={{ bg: crm.brandSoft }}
                aria-label="Criar processo"
                icon={<FaPlus />}
              />
            </Tooltip>
          )}
          {canManage && (
            <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
              <PopoverTrigger>
                <IconButton
                  onClick={onOpen}
                  aria-label="Processos"
                  icon={<FaArchive />}
                />
              </PopoverTrigger>
              <PopoverContent>
                <Box p="32px">
                <FormLabel>
                  Selecione um processo para ligar a ordem de compra
                </FormLabel>
                <ProcessInput width="100%" {...{ processId, setProcessId }} />

                <Button
                  type="submit"
                  mt="32px"
                  isLoading={loadingProcess}
                  onClick={() => setProcess()}
                  color="white"
                  borderRadius="10px"
                  width="120px"
                  backgroundColor={crm.brand}
                  _hover={{ backgroundColor: crm.brandDark }}
                >
                  Salvar
                </Button>
                </Box>
              </PopoverContent>
            </Popover>
          )}
          {canManage && (
            <IconButton
              isLoading={loadingDownload}
              onClick={() => navigate(`/savePurchaseOrders?id=${p.id}`)}
              // onClick={() => router}
              variant="ghost"
              color={crm.brand}
              _hover={{ bg: crm.brandSoft }}
              aria-label="Editar"
              icon={<FaPencilAlt />}
            />
          )}
          {canManage && (
            <IconButton
              isLoading={loadingDelete}
              variant="ghost"
              onClick={() => handleRemove()}
              color={crm.danger}
              _hover={{ bg: "#fef2f2" }}
              aria-label="Remover"
              icon={<FaTrash />}
            />
          )}
        </Flex>
        {/* 
          <Text fontSize="12px">CVF Distribuidora:</Text>
          <Badge
          onClick={() =>
                    auth.position === 2 && handleSign("Administration")
                  }
                  cursor="pointer"
                  colorScheme={p.administrationSigned ? "green" : "yellow"}
                  p="8px"
                >
                  {loading ? (
                    <Spinner />
                  ) : p.administrationSigned ? (
                    "Assinado"
                  ) : (
                    "Pendente"
                  )}
                </Badge>
                {p.administrationSigned && (
                  <>
                    <Box mt="12px" fontSize="12px">
                      {" "}
                      em:{" "}
                      {DateTime.fromISO(
                        p.administrationSignedDate ?? ""
                      ).toFormat("dd/LL/yyyy HH:mm")}
                    </Box>
                    <Box fontSize="12px" mt="4px">
                      {" "}
                      por: {p.administrationSignedBy}
                    </Box>
                  </>
                )}
              </Box>

              <Box>
                <Text fontSize="12px">Comercial:</Text>
                <Badge
                  onClick={() =>
                    (auth.position === 2 || auth.position === 4) &&
                    handleSign("Commercial")
                  }
                  cursor="pointer"
                  colorScheme={p.commercialSigned ? "green" : "yellow"}
                  p="8px"
                >
                  {loading ? (
                    <Spinner />
                  ) : p.commercialSigned ? (
                    "Assinado"
                  ) : (
                    "Pendente"
                  )}
                </Badge>
                {p.commercialSigned && (
                  <>
                    <Box mt="12px" fontSize="12px">
                      {" "}
                      em:{" "}
                      {DateTime.fromISO(p.commercialSignedDate ?? "").toFormat(
                        "dd/LL/yyyy HH:mm"
                      )}
                    </Box>
                    <Box fontSize="12px" mt="4px">
                      {" "}
                      por: {p.commercialSignedBy}
                    </Box>
                  </>
                )}
              </Box>

              <Box>
                <Text fontSize="12px">Financeiro:</Text>
                <Badge
                  onClick={() =>
                    (auth.position === 2 || auth.position === 5) &&
                    handleSign("Financial")
                  }
                  cursor="pointer"
                  colorScheme={p.financialSigned ? "green" : "yellow"}
                  p="8px"
                >
                  {loading ? (
                    <Spinner />
                  ) : p.financialSigned ? (
                    "Assinado"
                  ) : (
                    "Pendente"
                  )}
                </Badge>
                {p.financialSigned && (
                  <>
                    <Box mt="12px" fontSize="12px">
                      {" "}
                      em:{" "}
                      {DateTime.fromISO(p.financialSignedDate ?? "").toFormat(
                        "dd/LL/yyyy HH:mm"
                      )}
                    </Box>
                    <Box fontSize="12px" mt="4px">
                      {" "}
                      por: {p.financialSignedBy}
                    </Box>
                  </>
                )}
              </Box>

              <Box>
                <Text fontSize="12px">Operacional:</Text>
                <Badge
                  onClick={() =>
                    (auth.position === 2 || auth.position === 3) &&
                    handleSign("Operational")
                  }
                  cursor="pointer"
                  colorScheme={p.operationalSigned ? "green" : "yellow"}
                  p="8px"
                >
                  {loading ? (
                    <Spinner />
                  ) : p.operationalSigned ? (
                    "Assinado"
                  ) : (
                    "Pendente"
                  )}
                </Badge>
                {p.operationalSigned && (
                  <>
                    <Box mt="12px" fontSize="12px">
                      {" "}
                      em:{" "}
                      {DateTime.fromISO(p.operationalSignedDate ?? "").toFormat(
                        "dd/LL/yyyy HH:mm"
                      )}
                    </Box>
                    <Box fontSize="12px" mt="4px">
                      {" "}
                      por: {p.operationalSignedBy}
                    </Box>
                  </>
                )}
              </Box>
            </Flex>
          </PopoverContent>
        </Popover>
      </Flex>
      <Flex
        alignItems="center"
        gap="16px"
        cursor="pointer"
        onClick={() => onOpenDetail()}
      >
        <span className="material-symbols-outlined">article</span>
        */}
      </Flex>

      <Box
        style={{
          position: "fixed",
          top: "-200vh",
          left: "0",
          width: "100%",
          backgroundColor: "#ffffff",
          color: "#000000",
          visibility: loadingDownload ? "visible" : "hidden",
          opacity: loadingDownload ? 1 : 0,
          zIndex: -1000,
          transformOrigin: "top left",
          overflow: "hidden",
        }}
      >
        <PurchaseOrderPrint purchaseOrder={p} {...{ printRef }} />
      </Box>

      <ApproveModal
        isOpen={history.isOpen}
        onClose={history.onClose}
        entityId={p.id}
        entity="PurchaseOrder"
        title={`Alterações da PO ${p.poNumber}`}
        reviewAdminChanges
        onReviewed={get}
      />
    </Flex>
  );
};
export default PurchaseOrderCard;
